// Document-type metadata + filing/destination constants for the OCR pages.
// Extracted from pages/InvoiceOCR.jsx to keep that file under control.
//
// Drives the doc-type-aware UI: dropzone copy, contact-section labels,
// whether to render Line Items / Totals, and the primary "save" destination
// shown in the footer once extraction completes.
//
// `primaryAction.kind === 'draft'` → POST /api/ocr/{id}/create-draft
//                                    (existing bill/invoice flow)
// `primaryAction.kind === 'file'`  → POST /api/ocr/{id}/file with
//                                    {destination}, then navigate.

export const DOC_TYPE_META = {
  bill: {
    label: 'Bill',
    contactLabel: 'Vendor',
    showContact: true,
    showLineItems: true,
    showTotals: true,
    primaryAction: { kind: 'draft', label: 'Create Draft Bill', route: '/bills' },
  },
  invoice: {
    label: 'Invoice',
    contactLabel: 'Customer',
    showContact: true,
    showLineItems: true,
    showTotals: true,
    primaryAction: { kind: 'draft', label: 'Create Draft Invoice', route: '/invoices' },
  },
  receipt: {
    label: 'Receipt',
    contactLabel: 'Merchant',
    showContact: true,
    showLineItems: true,
    showTotals: true,
    primaryAction: { kind: 'file', destination: 'receipts', label: 'Save to Receipts' },
  },
  tax_return: {
    label: 'Tax Return',
    contactLabel: 'Taxpayer',
    showContact: false,
    showLineItems: false,
    showTotals: false,
    primaryAction: { kind: 'file', destination: 'tax_folder', label: 'Save to Tax Folder' },
  },
  bank_statement: {
    label: 'Bank Statement',
    contactLabel: 'Account Holder',
    showContact: false,
    showLineItems: false,
    showTotals: false,
    primaryAction: { kind: 'file', destination: 'banking', label: 'Import to Banking' },
  },
  w9_form: {
    label: 'W-9 Form',
    contactLabel: 'Vendor',
    showContact: true,
    showLineItems: false,
    showTotals: false,
    primaryAction: { kind: 'file', destination: 'vendors', label: 'Create Vendor Record' },
  },
  contract: {
    label: 'Contract',
    contactLabel: 'Counterparty',
    showContact: true,
    showLineItems: false,
    showTotals: false,
    primaryAction: { kind: 'file', destination: 'documents', label: 'Save to Documents' },
  },
  unknown: {
    label: 'Document',
    contactLabel: 'Party',
    showContact: false,
    showLineItems: false,
    showTotals: false,
    primaryAction: { kind: 'file', destination: 'documents', label: 'Save to Documents' },
  },
};

export const metaFor = (docType) => DOC_TYPE_META[docType] || DOC_TYPE_META.unknown;

// Smart-action id → file-destination mapping. Used by the SmartActionsBar
// click handler so non-bill/invoice actions route through /api/ocr/{id}/file
// and end up on the correct destination page.
export const SMART_ACTION_DESTINATION = {
  save_doc: 'documents',
  save_receipt: 'receipts',
  save_ar: 'documents', // A/R hand-off lives under invoices; treat as document filing for now
  create_vendor: 'vendors',
  track_1099: 'vendors',
  reconcile: 'banking',
  spot_unusual: 'banking',
  extract_dates: 'documents',
  summarize: 'documents',
  esign: 'documents',
  match_bank: 'banking',
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

// Doc-type options shown in the upload-step dropdown. Drives:
//  1. The "Will be saved to" hint shown below the dropzone.
//  2. The `document_type` form value sent to /api/ocr/upload — when this
//     is not 'auto' the backend respects it over the AI detector.
export const DOC_TYPE_CHOICES = [
  { value: 'auto',           label: 'Auto-detect',     destLabel: 'AI Buck picks the right destination',  destPath: null },
  { value: 'bill',           label: 'Bill',            destLabel: 'Bills',                                 destPath: '/bills' },
  { value: 'invoice',        label: 'Invoice',         destLabel: 'Invoices',                              destPath: '/invoices' },
  { value: 'receipt',        label: 'Receipt',         destLabel: 'Documents · Receipts',                  destPath: '/documents?folder=receipts' },
  { value: 'tax_return',     label: 'Tax Return',      destLabel: 'Tax Folder',                            destPath: '/documents?folder=tax' },
  { value: 'w9_form',        label: 'W-9 Form',        destLabel: 'Vendor Records',                        destPath: '/contacts' },
  { value: 'contract',       label: 'Contract',        destLabel: 'Documents',                             destPath: '/documents' },
  { value: 'bank_statement', label: 'Bank Statement',  destLabel: 'Banking Register',                      destPath: '/banking' },
];

// Destinations a scan can be filed to (matches backend /file enum).
// Used by the Filed badge + the "Move to…" overflow menu in Recent Scans.
export const FILING_DESTINATIONS = [
  { value: 'tax_folder', shortLabel: 'Tax' },
  { value: 'receipts',   shortLabel: 'Receipts' },
  { value: 'banking',    shortLabel: 'Banking' },
  { value: 'vendors',    shortLabel: 'Vendors' },
  { value: 'documents',  shortLabel: 'Documents' },
];
